import React, { useState } from "react";
import { fmtDate, fmtHM, fmtDur } from "./helpers";
import { resolveName } from "../utils/names";

/* ── Shared chip button ── */
export function Chip({ children, sel, onClick, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "7px 11px", borderRadius: 6,
        border: `2px solid ${sel ? color : "var(--border)"}`,
        background: sel ? `${color}15` : "var(--bg)",
        color: sel ? color : "var(--subtle)",
        fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
      }}
    >{children}</button>
  );
}

/* ── Bottom-sheet wrapper ── */
export function Sheet({ children, onClose, header }) {
  const [scrolled, setScrolled] = useState(false);
  const handleScroll = (e) => {
    setScrolled(e.target.scrollTop > 0);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,.8)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 10001, animation: "fadeIn .12s",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 520, maxHeight: "90vh",
          background: "var(--surface)", borderTop: "1px solid var(--border)",
          borderRadius: "14px 14px 0 0",
          display: "flex", flexDirection: "column",
          animation: "sheetUp .2s ease",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Top header area (fixed, not scrolling) */}
        <div style={{
          padding: "6px 16px 0",
          flexShrink: 0,
          position: "relative",
          zIndex: 20,
          boxShadow: scrolled ? "0 8px 28px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.2)" : "none",
          borderBottom: scrolled ? "1px solid var(--border)" : "1px solid transparent",
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        }}>
          {/* Drag handle */}
          <div style={{ width: 36, height: 4, background: "var(--border)", borderRadius: 2, margin: "6px auto 8px" }} />
          {header}
        </div>

        {/* Scrollable body */}
        <div
          onScroll={handleScroll}
          style={{
            padding: "0 16px 24px",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            flex: 1,
            zIndex: 10,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * DispatchModal — Step 1: dispatch a guard.
 * Only asks who is dispatching (operator picker). No guard assignment.
 *
 * Props:
 *   alarm: Firestore alarm object
 *   operators: string[]          — list from facility.operators
 *   nameAliases: object          — facility.nameAliases (variantes → canónico)
 *   onDispatch: (docID, opName) => void
 *   onCancel: () => void
 */
export default function DispatchModal({ alarm, operators = [], nameAliases, onDispatch, onCancel }) {
  const [op, setOp] = useState("");
  const [showMore, setShowMore] = useState(false);
  const ops = operators || [];

  const startMs = alarm.createdAt?.seconds ? alarm.createdAt.seconds * 1000 : null;

  return (
    <Sheet onClose={onCancel}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: "#dc262618", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
        }}>⚡</div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "var(--bright)" }}>Despachar guardia</div>
          <div style={{ fontSize: 11, color: "var(--muted)" }}>
            {alarm.zoneName}
            {startMs && ` · ${fmtDate(startMs)} ${fmtHM(startMs)}`}
            {alarm.duration != null && ` · ${fmtDur(alarm.duration)}`}
            {alarm.docID && ` · ID: ${alarm.docID}`}
          </div>
        </div>
      </div>

      {/* Operator selector */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "var(--subtle)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".04em" }}>
          ¿Quién despacha?
        </label>
        {ops.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
            {ops.slice(0, 4).map(o => (
              <Chip key={o} sel={op === o} onClick={() => setOp(o)} color="#3b82f6">{o}</Chip>
            ))}
            {ops.length > 4 && (
              !showMore ? (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShowMore(true); }}
                  style={{
                    padding: "6px 12px", fontSize: 10, fontWeight: 700,
                    background: "transparent", color: "var(--bright)",
                    border: "1px dashed #3b82f640", borderRadius: 6,
                    cursor: "pointer", transition: "all 0.2s"
                  }}
                >
                  + Más...
                </button>
              ) : (
                ops.slice(4).map(o => (
                  <Chip key={o} sel={op === o} onClick={() => setOp(o)} color="#3b82f6">{o}</Chip>
                ))
              )
            )}
          </div>
        )}
        <input
          className="v3-input"
          placeholder="Nombre del operador..."
          value={op}
          onChange={e => setOp(e.target.value)}
        />
      </div>

      <button
        type="button"
        onClick={() => {
          // Normaliza + aplica aliases + reutiliza variante existente (anti-duplicados)
          const cleanOp = resolveName(op, nameAliases?.operators, ops);
          if(cleanOp) onDispatch(alarm.docID, cleanOp);
        }}
        disabled={!op}
        style={{
          width: "100%", padding: 13, border: "none", borderRadius: 9,
          background: op ? "#dc2626" : "var(--border)",
          color: op ? "#fff" : "var(--muted)",
          fontSize: 13, fontWeight: 700,
          cursor: op ? "pointer" : "not-allowed",
        }}
      >
        Confirmar despacho
      </button>
    </Sheet>
  );
}

